import GlobalState from "../appStates";
import Transition from "../engine/animations/Transition";
import { RouletteSlot } from "../engine/components/RouletteSlot";
import EventDispatcher from "../engine/EventDispatcher";
import { orientation, windowInfo } from "../engine/utils";
import CardInfo from "./cards/CardData";

class PointerPosition  {
  
  private x: number;
  private y: number;

  constructor(event: MouseEvent | TouchEvent) {
    if (event.type === "mousedown" || event.type === "mouseup") {
      event = event as MouseEvent;
      this.x = event.clientX;
      this.y = event.clientY;
    } else {
      event = event as TouchEvent;
      if(event.type === "touchstart") {
        this.x = event.touches[0].clientX;
        this.y = event.touches[0].clientY;
      } else if(event.type === "touchend") {
        this.x = event.changedTouches[0].clientX;
        this.y = event.changedTouches[0].clientY;
      }
    } 
  }

  equals(other: PointerPosition): boolean {
    return this.x === other.x && this.y === other.y;
  }
}

export default class TarotCard {
  private static readonly flipAnimationConfig: KeyframeAnimationOptions = {
    duration: 1000,
    fill: "forwards",
    easing: "ease-in-out",
  };

  readonly cardInfo: CardInfo;
  private htmlImg: HTMLImageElement;

  private parent: RouletteSlot;
  private cardContainer: HTMLElement | null = null;
  private front: HTMLElement | null = null;
  private back: HTMLElement | null = null;

  private clickStartPosition: PointerPosition | null = null;
  private activated: boolean;

  private activationTransformation: {
    rotation: { z: number},
    scale: number,
    translation: { x: number, y: number }
  }

  constructor(cardInfo: CardInfo, htmlImg: HTMLImageElement) {
    this.cardInfo = cardInfo;
    this.htmlImg = htmlImg;
    this.activated = false;

    this.activationTransformation = {
      rotation: { z: 0 },
      scale: 2.875,
      translation: { x: 0, y: 0 }
    }

    EventDispatcher.registerEvent("card:activated");
    EventDispatcher.registerEvent("card:deactivated");
  }

  appendTo(parent: RouletteSlot): void {
    this.parent = parent;
    this.htmlImg.setAttribute("draggable", "false");

    this.cardContainer = document.createElement("div");
    this.cardContainer.classList.add("card-container");

    this.front = this.getFront();
    this.cardContainer.appendChild(this.front);

    this.back = this.getBack();
    this.cardContainer.appendChild(this.back);

    ["mousedown", "touchstart"].forEach((eventName) => {
      this.cardContainer.addEventListener(
        eventName,
        this.onMouseDown.bind(this)
      );  
    });

    ["mouseup", "touchend"].forEach((eventName) => {
      this.cardContainer.addEventListener(
        eventName,
        this.onMouseUp.bind(this)
      );
    });

    parent.appendChild(this.cardContainer);
  }

  private getBack(): HTMLElement {
    const div = document.createElement("div");
    div.classList.add("back", "side");

    const title = document.createElement("h2");
    title.classList.add("card-title");
    title.textContent = this.cardInfo.name;
    div.appendChild(title);

    const description = document.createElement("div");
    description.classList.add("card-description");
    this.cardInfo.description.forEach((paragraph) => {
      const pElement = document.createElement("p");
      pElement.innerText = paragraph;
      description.appendChild(pElement);
    });
    div.appendChild(description);
    return div;
  }

  private getFront(): HTMLElement {
    const div = document.createElement("div");
    div.classList.add("front", "side");
    div.appendChild(this.htmlImg);
    div.appendChild(this.htmlImg);

    return div;
  }

  onMouseDown(event: MouseEvent): void {
    this.clickStartPosition = new PointerPosition(event);
  }

  onMouseUp(event: MouseEvent): void {
    if (!this.clickStartPosition) return;
    const actualMousePosition = new PointerPosition(event);
    
    const activatedCard = GlobalState.get("activeCard");
    if(activatedCard !== null) return;

    if (actualMousePosition.equals(this.clickStartPosition) && !this.activated) {
      this.activated = true;
      this.flip();
      this.center();
      EventDispatcher.dispatch("card:activated", this);
    }
  }

  flip(): void {
    const frontRotation = this.activated ? 180 : 0;
    const backRotation = this.activated ? 360 : 180;
    this.front.animate(
      [{ transform: `rotateY(${frontRotation}deg)` }],
      TarotCard.flipAnimationConfig
    );
    
    this.back.animate(
      [{ transform: `rotateY(${backRotation}deg)` }],
      TarotCard.flipAnimationConfig
    );
  }

  async center() {
    const parentTransformation = this.parent.absoluteTransformation;
    this.activationTransformation.rotation.z = -parentTransformation.zRotation;
    
    const window = windowInfo();
    const screenCenter = new DOMPoint(
      window.width * 0.5,
      window.height * 0.5
    );
  
    if(orientation() === "portrait") {
      let currentScale = window.height / this.cardContainer.clientHeight;
      this.activationTransformation.scale = currentScale;
    }
    
    const actualPosition = this.position; 
    

    this.activationTransformation.translation = {
      x: (screenCenter.x - actualPosition.x)/this.activationTransformation.scale,
      y: (screenCenter.y - actualPosition.y)/this.activationTransformation.scale,
    };
    
    this.parent.bringToTop();
    this.cardContainer.animate(
      [
        {
          transform: `rotateZ(${this.activationTransformation.rotation.z}deg) 
            scale(${this.activationTransformation.scale}) 
            translate3d(${this.activationTransformation.translation.x}px, 
            ${this.activationTransformation.translation.y}px, 0)`,
        },
      ],
      TarotCard.flipAnimationConfig
    );
  }

  public async deactivate(): Promise<void> {
    console.log(this.activationTransformation)
    this.activated = false;
    EventDispatcher.dispatch("card:deactivated", this);
    this.flip();
    await this.cardContainer.animate(
      {
        transform: `rotateZ(0deg) scale(1) translate3d(0, 0, 0)`,
      },
      TarotCard.flipAnimationConfig
    ).finished;
    this.parent.bringToBack();
  }

  private get position(): DOMPoint {
    const boundingRect = this.cardContainer.getBoundingClientRect();
    return new DOMPoint(
      boundingRect.x + boundingRect.width / 2,
      boundingRect.y + boundingRect.height / 2
    );
  }
}
